import { MentorMenteeCard } from './MentorPortalService';
import { formatDayMonthYear, joinPresent } from './mentorDashboardFormat';

const GOALS_PLACEHOLDER = 'Goals will appear when the mentee submits registration';

type Props = {
    mentee: MentorMenteeCard | null;
};

const MentorMessagesSidebar = ({ mentee }: Props) => {
    if (!mentee) {
        return (
            <aside className="mmmsg-sidebar" aria-label="Mentee profile">
                <h2 className="mmmsg-pane-title">Mentee profile</h2>
                <p className="mmmsg-muted">Select a conversation.</p>
            </aside>
        );
    }
    const trackNames = (mentee.tracks || [])
        .map((track) => track.trackName)
        .filter((name): name is string => !!name);
    const programLine = joinPresent([mentee.program, mentee.batchYear]);
    const lastSession = formatDayMonthYear(mentee.lastSessionDate);
    const goalText = mentee.goals ? String(mentee.goals).trim() : '';

    const getInitials = (name: string) => {
        if (!name) return '';
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
    };

    return (
        <div
            className="col-md-3 p-3 bg-white d-flex flex-column justify-content-between"
            aria-label="Mentee profile"
            style={{ maxHeight: '580px', overflowY: 'auto' }}
        >
            <div className="text-center" id="chat-profile-panel">
                <div className="small fw-bold text-muted text-uppercase tracking-wider font-10 mb-3">
                    Mentee Profile
                </div>
                <span
                    className="rounded-circle border border-primary border-opacity-25 flex-shrink-0 d-flex justify-content-center align-items-center font-600 text-dark mx-auto mb-2"
                    style={{ width: '36px', height: '36px' }}
                >
                    {mentee.menteeName ? getInitials(mentee.menteeName) : 'C'}
                </span>
                {mentee.menteeName ? (
                    <h6 className="fw-bold mb-1 text-dark" id="chat-profile-name">
                        {mentee.menteeName}
                    </h6>
                ) : null}

                {programLine ? (
                    <div className="text-secondary small font-12" id="chat-profile-role">
                        {programLine}
                    </div>
                ) : null}
                <div className="mt-2">
                    <span className="badge bg-success bg-opacity-10 text-success fw-bold py-1 px-2 font-11 rounded-pill">
                        Paired with you
                    </span>
                </div>

                <hr className="my-3 border-top border-secondary border-opacity-75" />

                <div className="text-start">
                    {trackNames.length > 0 ? (
                        <>
                            <div className="small fw-bold text-muted text-uppercase tracking-wider font-10 mb-2">
                                Career Tracks
                            </div>
                            <div className="d-flex flex-wrap gap-1 mb-3" id="chat-profile-tracks">
                                {trackNames.map((name) => (
                                    <span
                                        key={name}
                                        className="badge bg-light text-secondary font-11 border border-light-subtle"
                                    >
                                        {name}
                                    </span>
                                ))}
                            </div>
                        </>
                    ) : null}

                    <div className="bg-light-subtle border border-light-subtle rounded-3 p-3 mb-2">
                        <div className="font-12 font-600 text-dark text-uppercase tracking-wider mb-0">
                            Mentorship Goals
                        </div>
                        <p
                            className="text-black font-14 mb-0 lh-base"
                            id="chat-profile-goals"
                        >
                            {goalText || GOALS_PLACEHOLDER}
                        </p>
                    </div>

                    <div className="bg-light-subtle border border-light-subtle rounded-3 p-3 mb-2">
                        <div className="font-12 font-600 text-dark text-uppercase tracking-wider mb-0">
                            Session History
                        </div>
                        <p className="text-black font-14 mb-0 lh-base" id="chat-profile-history">
                            {lastSession || 'No sessions logged'}
                        </p>
                    </div>
                    
                </div>
            </div>
        </div>
    );
};

export default MentorMessagesSidebar;
